import React, { useState, useEffect } from 'react';
import radiologyMasterConfigService from '../../services/radiologyMasterConfigService';
import './RadiologyConfigDashboard.css';

export default function RadiologyConfigDashboard({ onSelectInvestigation, onNavigateTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await radiologyMasterConfigService.getDashboard();
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError('Failed to load dashboard metrics.');
      }
    } catch (err) {
      console.error('Failed to load radiology dashboard:', err);
      setError('Unable to connect to radiology master configuration API.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="rad-dash-loading">
        <div className="rad-spinner"></div>
        <p>Loading Radiology Master Configuration Metrics...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rad-dash-error">
        <div className="rad-dash-error-icon">⚠️</div>
        <h3>Configuration Dashboard Unavailable</h3>
        <p>{error || 'No data found'}</p>
        <button type="button" className="rad-btn-retry" onClick={fetchDashboard}>
          🔄 Retry
        </button>
      </div>
    );
  }

  const {
    totalModalities,
    totalInvestigations,
    totalTemplates,
    defaultTemplates,
    missingTemplates,
    draftTemplates,
    importedTemplates,
    recentlyUpdatedCount,
    modalityBreakdown = [],
    unconfiguredInvestigations = [],
    recentlyUpdated = []
  } = data;

  // Calculate overall completion rate
  const overallCompletion = totalInvestigations > 0
    ? Math.round(((totalInvestigations - missingTemplates) / totalInvestigations) * 100)
    : 0;

  return (
    <div className="rad-dashboard-container">
      {/* Top Banner with Overall Health */}
      <div className="rad-dash-hero-banner">
        <div className="rad-dash-hero-content">
          <div className="rad-dash-hero-title">
            <h2>Radiology Master Configuration & Governance</h2>
            <p>
              High-level overview of imaging modalities, diagnostic investigations, clinical templates, and coverage health.
            </p>
          </div>
          <div className="rad-dash-hero-metrics">
            <div className="rad-hero-stat">
              <span className="rad-stat-value">{overallCompletion}%</span>
              <span className="rad-stat-label">System Readiness</span>
            </div>
            <div className="rad-hero-actions">
              <button
                type="button"
                className="rad-btn-hero-action primary"
                onClick={() => onNavigateTab('TEMPLATES')}
              >
                📑 Configure Templates
              </button>
              <button
                type="button"
                className="rad-btn-hero-action secondary"
                onClick={() => onNavigateTab('SMART_PHRASES')}
              >
                💬 Smart Phrases Master
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="rad-kpi-grid">
        <div className="rad-kpi-card">
          <div className="rad-kpi-icon modalities">🌐</div>
          <div className="rad-kpi-details">
            <span className="rad-kpi-number">{totalModalities}</span>
            <span className="rad-kpi-title">Modalities</span>
            <span className="rad-kpi-sub">Active departments</span>
          </div>
        </div>

        <div className="rad-kpi-card">
          <div className="rad-kpi-icon investigations">🔬</div>
          <div className="rad-kpi-details">
            <span className="rad-kpi-number">{totalInvestigations}</span>
            <span className="rad-kpi-title">Investigations</span>
            <span className="rad-kpi-sub">Tests in catalog</span>
          </div>
        </div>

        <div className="rad-kpi-card">
          <div className="rad-kpi-icon templates">📑</div>
          <div className="rad-kpi-details">
            <span className="rad-kpi-number">{totalTemplates}</span>
            <span className="rad-kpi-title">Total Templates</span>
            <span className="rad-kpi-sub">{importedTemplates} Word imported</span>
          </div>
        </div>

        <div className="rad-kpi-card highlight-green">
          <div className="rad-kpi-icon defaults">⭐</div>
          <div className="rad-kpi-details">
            <span className="rad-kpi-number">{defaultTemplates}</span>
            <span className="rad-kpi-title">Default Templates</span>
            <span className="rad-kpi-sub">Ready for 1-click auto-load</span>
          </div>
        </div>

        <div className={`rad-kpi-card ${missingTemplates > 0 ? 'highlight-amber' : ''}`}>
          <div className="rad-kpi-icon missing">⚠️</div>
          <div className="rad-kpi-details">
            <span className="rad-kpi-number">{missingTemplates}</span>
            <span className="rad-kpi-title">Missing Templates</span>
            <span className="rad-kpi-sub">Investigations needing setup</span>
          </div>
        </div>

        <div className="rad-kpi-card">
          <div className="rad-kpi-icon updated">🕒</div>
          <div className="rad-kpi-details">
            <span className="rad-kpi-number">{recentlyUpdatedCount}</span>
            <span className="rad-kpi-title">Recently Updated</span>
            <span className="rad-kpi-sub">Active revisions</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Modality Health vs Missing Investigations & Recent Updates */}
      <div className="rad-dash-grid">
        {/* Modality Health & Completion Status */}
        <div className="rad-dash-card">
          <div className="rad-dash-card-header">
            <div>
              <h3>Modality Coverage Status</h3>
              <p>Percentage of investigations mapped to at least one active reporting template</p>
            </div>
            <button
              type="button"
              className="rad-dash-refresh-btn"
              onClick={fetchDashboard}
              title="Refresh metrics"
            >
              🔄 Refresh
            </button>
          </div>

          <div className="rad-modality-list">
            {modalityBreakdown.length === 0 ? (
              <div className="rad-empty-notice">No modality data available.</div>
            ) : (
              modalityBreakdown.map((m) => (
                <div key={m.modalityId} className="rad-modality-row">
                  <div className="rad-modality-info">
                    <span className="rad-modality-badge">{m.modalityName || m.departmentName}</span>
                    <span className="rad-modality-counts">
                      {m.investigationCount} Investigations • {m.templateCount} Templates • {m.defaultTemplateCount} Defaults
                    </span>
                  </div>
                  <div className="rad-progress-wrap">
                    <div className="rad-progress-bar-bg">
                      <div
                        className={`rad-progress-fill ${
                          m.completionPercentage === 100
                            ? 'complete'
                            : m.completionPercentage > 50
                            ? 'in-progress'
                            : 'low'
                        }`}
                        style={{ width: `${m.completionPercentage}%` }}
                      ></div>
                    </div>
                    <span className="rad-progress-label">{m.completionPercentage}%</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Missing Configurations & Quick Action */}
        <div className="rad-dash-card">
          <div className="rad-dash-card-header">
            <div>
              <h3>Incomplete Configurations</h3>
              <p>Investigations that currently have 0 templates mapped</p>
            </div>
            <span className={`rad-tag-badge ${unconfiguredInvestigations.length === 0 ? 'good' : 'warning'}`}>
              {unconfiguredInvestigations.length === 0 ? '✓ Complete' : `${unconfiguredInvestigations.length} Missing`}
            </span>
          </div>

          <div className="rad-unconfigured-list">
            {unconfiguredInvestigations.length === 0 ? (
              <div className="rad-all-complete-state">
                <div className="rad-complete-icon">🎉</div>
                <h4>All Investigations Configured!</h4>
                <p>Every active radiology investigation has at least one configured reporting template.</p>
                <button
                  type="button"
                  className="rad-btn-action-small"
                  onClick={() => onNavigateTab('TEMPLATES')}
                >
                  View All Templates →
                </button>
              </div>
            ) : (
              <div className="rad-incomplete-items">
                {unconfiguredInvestigations.map((inv) => (
                  <div key={inv.investigationId} className="rad-incomplete-item">
                    <div className="rad-incomplete-meta">
                      <span className="rad-incomplete-code">{inv.testCode || 'INV'}</span>
                      <strong className="rad-incomplete-name">{inv.investigationName}</strong>
                      <span className="rad-incomplete-dept">{inv.departmentName}</span>
                    </div>
                    <button
                      type="button"
                      className="rad-btn-configure-now"
                      onClick={() => onSelectInvestigation && onSelectInvestigation(inv)}
                    >
                      + Configure Template
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recently Updated Templates Table */}
      <div className="rad-dash-card recent-revisions-card">
        <div className="rad-dash-card-header">
          <div>
            <h3>Recently Updated Templates & Revisions</h3>
            <p>Audit trail of templates modified, updated, or re-versioned across all modalities</p>
          </div>
          <button
            type="button"
            className="rad-dash-viewall-btn"
            onClick={() => onNavigateTab('TEMPLATES')}
          >
            Manage Library →
          </button>
        </div>

        <div className="rad-recent-table-wrap">
          <table className="rad-recent-table">
            <thead>
              <tr>
                <th>Template Title</th>
                <th>Investigation</th>
                <th>Modality</th>
                <th>Version</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {recentlyUpdated.length === 0 ? (
                <tr>
                  <td colSpan="7" className="rad-table-empty">No recent revisions found.</td>
                </tr>
              ) : (
                recentlyUpdated.slice(0, 8).map((t) => (
                  <tr key={t.templateId}>
                    <td className="rad-td-title">
                      <strong>{t.templateTitle}</strong>
                      {t.isDefault && <span className="rad-mini-badge-default">⭐ Default</span>}
                    </td>
                    <td>{t.investigationName}</td>
                    <td><span className="rad-badge-mod">{t.departmentName}</span></td>
                    <td><span className="rad-badge-version">v{t.version || 1}</span></td>
                    <td><span className="rad-badge-status-active">Active</span></td>
                    <td className="rad-td-date">
                      {t.updatedAt ? new Date(t.updatedAt).toLocaleString() : 'Recently'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="rad-table-btn"
                        onClick={() => onNavigateTab('TEMPLATES')}
                      >
                        Open In Library
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
